import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import { SearchAnchor } from '../search-anchor';
import { default as SearchInput } from './search-input';
import { SEARCH_QUERY_KEY, setSessionItem, getSessionItem } from './session-storage-utils';
import { getInputSkeletonQuery, setInputSkeletonQuery, getInputSkeletonFocus, setInputSkeletonIsFocus, getInputSkeletonSelection, setInputSkeletonSelection, resetInputSkeletonState, removeInputSkeletonEventListeners } from './query-store';
/**
 * An interactive search input skeleton that can be used as a placeholder. The
 * state of this input is stored in the browser's window object, and uses
 * the stored state as its initial state on mount.
 */
export var SearchInputSkeletonInteractive = function SearchInputSkeletonInteractive(_ref) {
  var placeholder = _ref.placeholder,
    stickySearchEnabled = _ref.stickySearchEnabled,
    onEnter = _ref.onEnter,
    shouldFillContainer = _ref.shouldFillContainer;
  // Initialise stored inputSkeletonState if necessary
  var checkedForInputSkeletonState = useRef(false);
  if (!checkedForInputSkeletonState.current) {
    if (!window.inputSkeletonState) {
      resetInputSkeletonState();
    }
    checkedForInputSkeletonState.current = true;
  }
  var searchInputRef = useRef(null);

  // Use the stored state as initial values for expansion and query
  var _useState = useState(getInputSkeletonFocus()),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var _useState3 = useState(function () {
      var stickySearchQuery = getSessionItem(SEARCH_QUERY_KEY);
      return stickySearchEnabled && stickySearchQuery ? stickySearchQuery : getInputSkeletonQuery();
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    value = _useState4[0],
    setValue = _useState4[1];

  // Use the stored state to focus and set selection
  useLayoutEffect(function () {
    removeInputSkeletonEventListeners();
    var isFoucsed = getInputSkeletonFocus();
    var _getInputSkeletonSele = getInputSkeletonSelection(),
      selectionStart = _getInputSkeletonSele.selectionStart,
      selectionEnd = _getInputSkeletonSele.selectionEnd,
      selectionDirection = _getInputSkeletonSele.selectionDirection;
    if (isFoucsed) {
      var _searchInputRef$curre, _searchInputRef$curre2;
      searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
      searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre2 = searchInputRef.current) === null || _searchInputRef$curre2 === void 0 || _searchInputRef$curre2.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
    }
  }, []);
  var onFocus = function onFocus() {
    var _searchInputRef$curre3;
    setIsExpanded(true);
    setInputSkeletonIsFocus(true);
    searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre3 = searchInputRef.current) === null || _searchInputRef$curre3 === void 0 || _searchInputRef$curre3.focus();
  };
  var onBlur = function onBlur() {
    setIsExpanded(false);
    setInputSkeletonIsFocus(false);
    if (!stickySearchEnabled) {
      setValue('');
      setInputSkeletonQuery('');
      setInputSkeletonSelection(0, 0, 'none');
    }
  };
  var onKeyDown = function onKeyDown(e) {
    if (e.key === 'Escape') {
      var _searchInputRef$curre4;
      searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre4 = searchInputRef.current) === null || _searchInputRef$curre4 === void 0 || _searchInputRef$curre4.blur();
      onBlur();
    }
  };
  var onInput = function onInput(newValue) {
    var _searchInputRef$curre5, _searchInputRef$curre6, _searchInputRef$curre7;
    setValue(newValue);
    setInputSkeletonQuery(newValue);
    setInputSkeletonSelection((searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre5 = searchInputRef.current) === null || _searchInputRef$curre5 === void 0 ? void 0 : _searchInputRef$curre5.selectionStart) || 0, (searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre6 = searchInputRef.current) === null || _searchInputRef$curre6 === void 0 ? void 0 : _searchInputRef$curre6.selectionEnd) || 0, (searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre7 = searchInputRef.current) === null || _searchInputRef$curre7 === void 0 ? void 0 : _searchInputRef$curre7.selectionDirection) || 'none');
    if (stickySearchEnabled) {
      setSessionItem(SEARCH_QUERY_KEY, newValue);
    }
  };
  var onSelect = function onSelect() {
    var _searchInputRef$curre8, _searchInputRef$curre9, _searchInputRef$curre0;
    setInputSkeletonSelection((searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre8 = searchInputRef.current) === null || _searchInputRef$curre8 === void 0 ? void 0 : _searchInputRef$curre8.selectionStart) || 0, (searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre9 = searchInputRef.current) === null || _searchInputRef$curre9 === void 0 ? void 0 : _searchInputRef$curre9.selectionEnd) || 0, (searchInputRef === null || searchInputRef === void 0 || (_searchInputRef$curre0 = searchInputRef.current) === null || _searchInputRef$curre0 === void 0 ? void 0 : _searchInputRef$curre0.selectionDirection) || 'none');
  };

  // Fixes some instances where callbacks would not be called when the search
  // input is clicked as the component is being destroyed
  useEffect(function () {
    var storedSearchInputRef = null;
    if (searchInputRef.current) {
      storedSearchInputRef = searchInputRef.current;
    }
    return function () {
      if (storedSearchInputRef) {
        setInputSkeletonQuery(storedSearchInputRef.value);
        setInputSkeletonIsFocus(document.activeElement === storedSearchInputRef);
        setInputSkeletonSelection(storedSearchInputRef.selectionStart || 0, storedSearchInputRef.selectionEnd || 0, storedSearchInputRef.selectionDirection || 'none');
      }
    };
  }, []);
  return /*#__PURE__*/React.createElement(SearchAnchor, {
    isExpanded: isExpanded,
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyDown: onKeyDown,
    shouldFillContainer: shouldFillContainer
  }, /*#__PURE__*/React.createElement(SearchInput, {
    "data-test-id": "search-dialog-input-skeleton-interactive",
    isExpanded: isExpanded,
    placeholder: placeholder,
    onInput: onInput,
    onSelect: onSelect,
    onEnter: onEnter,
    shouldFillContainer: shouldFillContainer,
    value: value,
    ref: searchInputRef
  }));
};